import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import {
    Box,
    IconButton,
    Menu,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useAccount } from '../../context/account_context'
import type { AccountInfo } from '../../context/account_context'
import { BranchInfo, useBranch } from '../../context/branch_context'
import { useCoin } from '../../context/coin_context'
import { SystemBranchEnum, useConfigs } from '../../context/configs_context'
import { useLoading } from '../../context/loading_context'
import {
    BranchStock,
    BranchStockType,
    BranchStockTypeLabel,
    Group,
    IvaEnum,
    Mark,
    Model,
    NamedPrice,
    ProductHistoricInfo,
    ProductHistoricTypeLabel,
    ProductInfo,
    SubGroup,
    useProduct,
} from '../../context/product_context'
import { useSnack } from '../../context/snack_context'
import { getFileEndpoint } from '../../services/media'
import { getModelById } from '../../services/model'
import { listProductHistoric } from '../../services/product'
import { getSubGroupById } from '../../services/subgroup'
import { listAccounts } from '../../services/account'
import { getDateString, getTimeAgo, maskCurrency } from '../../utils/maks'
import { CustomDivider } from '../CustomDivider'
import { GenericTable } from '../GenericTable'
import { SubtitleHeader } from '../SubtitleHeader'
import { SupplierInfo } from '../../context/supplier_context'
import { ClientInfo } from '../../context/client_context'
import { InfoOutlined, InfoRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { EmployeeInfo } from '../../pages/Employee/Home'
const currency = require('currency.js')

interface Props {
    product: ProductInfo
}

export interface ProductHistoricFilters {
    products: ProductInfo[]
    clients?: ClientInfo[]
    page: number
    rows: number
    rowsCount: number
    type?: string
    date?: {
        iniD: Date
        endD: Date
    } | null
    employees?: EmployeeInfo[]
    branches?: BranchInfo[]
    users?: AccountInfo[]
}

export interface ProductHistoricTotal {
    branch: BranchInfo | null
    totalIn: number
    totalOut: number
    currentStock: number
    initialStock: number
}

export const ProductDetails = ({ product }: Props) => {
    const { t } = useTranslation()
    const {
        id,
        image,
        name,
        selectedGroup: group,
        selectedSubGroup: subgroup,
        selectedMark: mark,
        selectedModel: model,
        stock,
        minStock,
        idealStock,
        aplication,
        observation,
        barcodes,
        unitType,
        buyPrice,
        salePrice,
        porcentGain,
        qntPrice: qntPrices,
        namedPrice: namedPrices,
        iva,
        proporcaoIva,
        showIva,
        sellWithoutStock,
        isAtive: isActive,
        autoSum,
        autoPrice,
        branchStocks,
        supplier,
        localizations,
        productsStockAfterOperation,
        cifPrice,
        avgBuyPrice,
        avgCifPrice,
        realWeight,
    } = product
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
    const [selectedSubGroup, setSelectedSubGroup] = useState<SubGroup | null>(
        null
    )
    const [selectedMark, setSelectedMark] = useState<Mark | null>(null)
    const [selectedModel, setSelectedModel] = useState<Model | null>(null)
    const loading = useLoading()
    const snack = useSnack()
    const { productInfo } = useProduct()
    const { coins } = useCoin()
    const { branches } = useBranch()
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()

    const [productHistoric, setProductHistoric] = useState<
        ProductHistoricInfo[]
    >([])
    const [productHistoricFilters, setProductHistoricFilters] =
        useState<ProductHistoricFilters>({
            products: [product],
            page: 1,
            rows: 10,
            rowsCount: 0,
        })
    const [productHistoricTotals, setProductHistoricTotals] = useState<
        ProductHistoricTotal[]
    >([])
    const [anchor, setAnchor] = useState<HTMLElement | null>(null)
    const selectedProductHistoric = useRef<ProductHistoricInfo | null>(null)
    const [users, setUsers] = useState<AccountInfo[]>([])
    const usersTimeout = useRef<NodeJS.Timeout>()

    const canSeeAllBranches =
        !accountInfo?.branch && configs.systemBranch === SystemBranchEnum.Branch

    const searchAccounts = async (value: string) => {
        if (!value) return
        try {
            const res = await listAccounts(
                {
                    page: 1,
                    rows: 10,
                    search: value,
                },
                accountInfo?.rules ?? [],
                coins
            )
            setUsers(res.data)
        } catch (error) {}
    }

    const canListProductHistoric =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'listar histórico'
        ) !== -1

    const productHistoricPaginated = productHistoric.slice(
        (productHistoricFilters.page - 1) * productHistoricFilters.rows,
        productHistoricFilters.page * productHistoricFilters.rows
    )

    useEffect(() => {
        populateData()
    }, [])

    useEffect(() => {
        if (!coins.length || !canListProductHistoric || !configs.id) return

        handleListProductHistoric({
            ...productHistoricFilters,
            rows: configs.paginationRowsNumber,
        })
    }, [coins])

    const handleListProductHistoric = async (
        filters: ProductHistoricFilters
    ) => {
        loading.logicShow()
        try {
            const res = await listProductHistoric(
                {
                    id_produto: filters.products.map((p) => p.id as number),
                    tipo: filters.type,
                    id_filial: filters.branches
                        ?.map((b) => b.id)
                        .filter((id): id is number => !!id),
                    id_usuario: filters.users
                        ?.map((u) => u.id)
                        .filter((id): id is number => !!id),
                },
                coins
            )
            setProductHistoric(res.data)
            setProductHistoricFilters({
                ...filters,
                rowsCount: res.count ? res.count : filters.rowsCount,
            })

            const totals: ProductHistoricTotal[] = []

            for (const productHistoric of res.data) {
                const branch =
                    branches.find(
                        (b) => b.name === productHistoric.branchName
                    ) ?? null

                const foundTotal = totals.find(
                    (t) => t.branch?.id === branch?.id
                )

                if (foundTotal) {
                    if (productHistoric.qnt > 0)
                        foundTotal.totalIn += productHistoric.qnt
                    else foundTotal.totalOut += productHistoric.qnt

                    foundTotal.initialStock += productHistoric.qnt * -1
                } else {
                    const currStock =
                        product.branchStocks?.find(
                            (bs) => bs.branchId === branch?.id && bs.type === BranchStockType.Normal
                        )?.stock ??
                        product.stock ??
                        0

                    const newTotal: ProductHistoricTotal = {
                        branch,
                        totalIn: 0,
                        totalOut: 0,
                        initialStock: currStock,
                        currentStock: currStock,
                    }

                    if (productHistoric.qnt > 0)
                        newTotal.totalIn += productHistoric.qnt
                    else newTotal.totalOut += productHistoric.qnt

                    newTotal.initialStock += productHistoric.qnt * -1

                    totals.push(newTotal)
                }
            }

            setProductHistoricTotals(totals)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const populateData = async () => {
        loading.show()
        try {
            if (group && !group.name) {
                const populatedGroup = productInfo.groups.find(
                    (g) => g.id === group.id
                )
                populatedGroup && setSelectedGroup(populatedGroup)
            }
            if (mark && !mark.name) {
                const populatedMark = productInfo.marks.find(
                    (m) => m.id === mark.id
                )
                populatedMark && setSelectedMark(populatedMark)
            }
            if (subgroup && !subgroup.name) {
                const populatedSubGroup = await getSubGroupById(subgroup.id)
                setSelectedSubGroup(populatedSubGroup)
            }
            if (model && !model.name) {
                const populatedModel = await getModelById(model.id)
                setSelectedModel(populatedModel)
            }
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const getBarcodesString = () => {
        let barcodesString = ''
        for (let i = 0; i < barcodes.length; i++) {
            if (i < barcodes.length - 1) barcodesString += `${barcodes[i]}, `
            else barcodesString += `${barcodes[i]}`
        }
        return barcodesString
    }

    const productHistoricbranchColumn =
        !accountInfo?.branch && configs.systemBranch === SystemBranchEnum.Branch
            ? [
                  {
                      name: 'Sucursal',
                      property: ['branchName'],
                      parser: (value: any) => value ?? '---',
                      hideXs: true,
                  },
              ]
            : []

    const canShowStock =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'listar estoque'
        ) !== -1

    return (
        <>
            <Menu
                open={Boolean(anchor)}
                onClose={() => {
                    setAnchor(null)
                    selectedProductHistoric.current = null
                }}
                anchorEl={anchor}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    top: '-95px',
                    left: '30px',
                }}
            >
                <Box sx={{ px: '15px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography>
                            {t('Moneda')}:{' '}
                            {
                                selectedProductHistoric.current?.exchange?.coin
                                    .name
                            }{' '}
                            (
                            {
                                selectedProductHistoric.current?.exchange?.coin
                                    .name
                            }
                            )
                        </Typography>
                        <Box
                            component="img"
                            src={`/flags/${selectedProductHistoric.current?.exchange?.coin.pathImage}`}
                            sx={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                ml: '5px',
                            }}
                        />
                    </Box>
                    <Box>
                        {t('Câmbio')}:{' '}
                        {selectedProductHistoric.current?.exchange
                            ?.flagReverseExchange
                            ? selectedProductHistoric.current.coin?.symbol
                            : selectedProductHistoric.current?.exchange?.coin
                                  ?.symbol}{' '}
                        {selectedProductHistoric.current?.exchange &&
                            maskCurrency(
                                selectedProductHistoric.current?.exchange
                                    ?.exchange,
                                selectedProductHistoric.current?.exchange
                                    ?.flagReverseExchange
                                    ? selectedProductHistoric.current?.coin
                                    : selectedProductHistoric.current?.exchange
                                          ?.coin
                            )}
                    </Box>
                    <Box>
                        {t('Valor')}:{' '}
                        {
                            selectedProductHistoric.current?.exchange?.coin
                                ?.symbol
                        }{' '}
                        {selectedProductHistoric.current?.exchange &&
                            maskCurrency(
                                selectedProductHistoric.current?.exchange
                                    ?.amount,
                                selectedProductHistoric.current?.exchange?.coin
                            )}
                    </Box>
                </Box>
            </Menu>
            <Stack direction="row">
                <Box
                    component="img"
                    sx={{ width: '80px', height: '80px', borderRadius: '50%' }}
                    src={
                        !image?.isBase64 && image?.content
                            ? getFileEndpoint(image?.content)
                            : image?.content
                              ? image?.content
                              : ''
                    }
                />
                <Box sx={{ ml: '20px' }}>
                    <Typography sx={{ fontSize: '22px' }}>{name}</Typography>
                    {group && (
                        <Typography sx={{ fontSize: '18px' }}>
                            {group.name ? group.name : selectedGroup?.name}
                            {subgroup &&
                                `, ${
                                    subgroup.name
                                        ? subgroup.name
                                        : selectedSubGroup?.name
                                }`}
                        </Typography>
                    )}
                </Box>
            </Stack>
            <CustomDivider />
            <Typography
                sx={{ fontSize: '18px', my: '10px', fontWeight: '600' }}
            >
                {t('Detalles del producto')}
            </Typography>
            {mark && (
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {t('Marca')}:{' '}
                        </Box>{' '}
                        {mark.name ? mark.name : selectedMark?.name}
                    </Typography>
                    {model && (
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Modelo')}:{' '}
                            </Box>{' '}
                            {model.name ? model.name : selectedModel?.name}
                        </Typography>
                    )}
                </Stack>
            )}
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Códigos de barra')}:{' '}
                    </Box>{' '}
                    {getBarcodesString()}
                </Typography>
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Códigos internos')}:{' '}
                    </Box>{' '}
                    {product.internalCodes.join(',') || '---'}
                </Typography>
            </Stack>

            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Peso')}:{' '}
                    </Box>{' '}
                    {realWeight} kg
                </Typography>
                {(!accountInfo?.isMaster ||
                    configs.systemBranch !== SystemBranchEnum.Branch) &&
                    typeof stock === 'number' && (
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Stock')}:{' '}
                            </Box>{' '}
                            {canShowStock ? stock : '---'}
                        </Typography>
                    )}
            </Stack>
            {branchStocks && branchStocks.length > 0 && (
                <>
                    <Typography>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {t('Stock en sucursales')}:{' '}
                        </Box>
                    </Typography>
                    <Table
                        sx={{
                            width: { xs: '75%', sm: '45%', lg: '45%' },
                            mt: '5px',
                            border: '1px solid rgba(224, 224, 224, 1)',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Sucursal')}
                                </TableCell>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Stock')}
                                </TableCell>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Tipo')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {branchStocks.map((branchStock) => (
                                <TableRow>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {
                                            branches.find(
                                                (b) =>
                                                    b.id ===
                                                    branchStock.branchId
                                            )?.name
                                        }
                                    </TableCell>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {branchStock.stock}
                                    </TableCell>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {BranchStockTypeLabel[branchStock.type]}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
            {Boolean(minStock || idealStock) && (
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    {minStock && (
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Stock minimo')}:{' '}
                            </Box>{' '}
                            {minStock}
                        </Typography>
                    )}
                    {idealStock && (
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Stock ideal')}:{' '}
                            </Box>{' '}
                            {idealStock}
                        </Typography>
                    )}
                </Stack>
            )}
            {Boolean(aplication || observation) && (
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    {aplication && (
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Solicitud')}:{' '}
                            </Box>{' '}
                            {aplication}
                        </Typography>
                    )}
                    {observation && (
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Observación')}:{' '}
                            </Box>{' '}
                            {observation}
                        </Typography>
                    )}
                </Stack>
            )}
            {Boolean(supplier) && (
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    {supplier && (
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Proveedor')}:{' '}
                            </Box>{' '}
                            {supplier.name}
                        </Typography>
                    )}
                </Stack>
            )}
            <CustomDivider />
            <Typography
                sx={{ fontSize: '18px', my: '10px', fontWeight: '600' }}
            >
                {t('Precios del produto')}
            </Typography>
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Precio de compra')}:{' '}
                    </Box>{' '}
                    {`${product.coin?.symbol} ${maskCurrency(
                        buyPrice,
                        product.coin
                    )}`}
                </Typography>
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Precio de venda')}:{' '}
                    </Box>{' '}
                    {`${product.coin?.symbol} ${maskCurrency(
                        salePrice,
                        product.coin
                    )}`}
                </Typography>
            </Stack>
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Porcentaje de beneficio estimado')}:{' '}
                    </Box>{' '}
                    {porcentGain + '%'}
                </Typography>
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Unidad de medida')}:{' '}
                    </Box>{' '}
                    {unitType}
                </Typography>
            </Stack>
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Costo CIF')}:{' '}
                    </Box>{' '}
                    {product.coin?.symbol}{' '}
                    {maskCurrency(
                        currency(buyPrice).add(cifPrice).value,
                        product.coin
                    )}
                </Typography>
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Costo FOB médio')}:{' '}
                    </Box>{' '}
                    {product.coin?.symbol}{' '}
                    {maskCurrency(currency(avgBuyPrice).value, product.coin)}
                </Typography>
            </Stack>
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Costo CIF médio')}:{' '}
                    </Box>{' '}
                    {product.coin?.symbol}{' '}
                    {maskCurrency(
                        currency(avgBuyPrice).add(avgCifPrice).value,
                        product.coin
                    )}
                </Typography>
            </Stack>
            {qntPrices.length > 0 && (
                <>
                    <Typography>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {t('Precios en cantidad')}:{' '}
                        </Box>
                    </Typography>
                    <Table
                        sx={{
                            width: { xs: '75%', sm: '45%', lg: '20%' },
                            mt: '5px',
                            border: '1px solid rgba(224, 224, 224, 1)',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Precio')}
                                </TableCell>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Cantidad')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {qntPrices.map((qntPrice) => (
                                <TableRow>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >{`${product.coin?.symbol} ${maskCurrency(
                                        qntPrice.price,
                                        product.coin
                                    )}`}</TableCell>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {qntPrice.qnt}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
            {namedPrices.length > 0 && (
                <>
                    <Typography>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {t('Lista de precios')}:{' '}
                        </Box>
                    </Typography>
                    <Table
                        sx={{
                            width: { xs: '75%', sm: '45%', lg: '45%' },
                            mt: '5px',
                            border: '1px solid rgba(224, 224, 224, 1)',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Número')}
                                </TableCell>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Precio')}
                                </TableCell>
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Porc. lucro')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {namedPrices.map((namedPrice) => (
                                <TableRow>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {namedPrice.name}
                                    </TableCell>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >{`${product.coin?.symbol} ${maskCurrency(
                                        namedPrice.price,
                                        product.coin
                                    )}`}</TableCell>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {namedPrice.porcentGain}%
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
            <CustomDivider />
            <Typography
                sx={{ fontSize: '18px', my: '10px', fontWeight: '600' }}
            >
                {t('Otras informaciones')}
            </Typography>
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        IVA:{' '}
                    </Box>{' '}
                    {iva === IvaEnum.Exento
                        ? 'Exento'
                        : iva === IvaEnum.Cinco
                          ? '5%'
                          : proporcaoIva && proporcaoIva < 100
                            ? `10% (${proporcaoIva}% do valor)`
                            : '10%'}
                </Typography>
                {/* <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        Mostrar iva en factura:{' '}
                    </Box>{' '}
                    {showIva ? 'Sim' : 'Não'}
                </Typography>*/}
            </Stack>
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Vender sin stock')}:{' '}
                    </Box>{' '}
                    {t(sellWithoutStock ? 'Sí' : 'No')}
                </Typography>
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Producto activo')}:{' '}
                    </Box>{' '}
                    {t(isActive ? 'Sí' : 'No')}
                </Typography>
            </Stack>
            <Stack
                sx={{ width: '100%' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
            >
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('Autosuma')}:{' '}
                    </Box>{' '}
                    {t(autoSum ? 'Sí' : 'No')}
                </Typography>
                <Typography>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {t('AutoPrecio')}:{' '}
                    </Box>{' '}
                    {t(autoPrice ? 'Sí' : 'No')}
                </Typography>
            </Stack>
            {localizations && localizations.length > 0 && (
                <>
                    <Typography>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {t('Localizaciónes')}:{' '}
                        </Box>
                    </Typography>
                    <Table
                        sx={{
                            width: { xs: '75%', sm: '45%', lg: '45%' },
                            mt: '5px',
                            border: '1px solid rgba(224, 224, 224, 1)',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {branches.length > 0 && (
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {t('Sucursal')}
                                    </TableCell>
                                )}
                                <TableCell
                                    sx={{ textAlign: 'center', p: '8px' }}
                                >
                                    {t('Valor')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {localizations.map((localization) => (
                                <TableRow>
                                    {branches.length > 0 && (
                                        <TableCell
                                            sx={{
                                                textAlign: 'center',
                                                p: '8px',
                                            }}
                                        >
                                            {
                                                branches.find(
                                                    (b) =>
                                                        b.id ===
                                                        localization.branchId
                                                )?.name
                                            }
                                        </TableCell>
                                    )}
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {localization.value}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
            {productsStockAfterOperation &&
                productsStockAfterOperation.length > 0 && (
                    <>
                        <Typography>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {t('Productos pós venta')}:{' '}
                            </Box>
                        </Typography>
                        <Table
                            sx={{
                                width: { xs: '75%', sm: '45%', lg: '45%' },
                                mt: '5px',
                                border: '1px solid rgba(224, 224, 224, 1)',
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {t('Producto')}
                                    </TableCell>
                                    <TableCell
                                        sx={{ textAlign: 'center', p: '8px' }}
                                    >
                                        {t('Cantidad')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {productsStockAfterOperation.map((v) => (
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                textAlign: 'center',
                                                p: '8px',
                                            }}
                                        >
                                            {v.product?.name}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textAlign: 'center',
                                                p: '8px',
                                            }}
                                        >
                                            {v.qnt}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            <CustomDivider />
            <SubtitleHeader sx={{ mt: '15px', mb: '5px' }}>
                {t('Histórico del producto')}
            </SubtitleHeader>
            <GenericTable
                title="4aea99b6-c18b-4e76-bf53-78f0dd25d508"
                filters={[
                    {
                        name: 'Tipo',
                        labelField: 'name',
                        valueField: 'value',
                        values: Object.keys(ProductHistoricTypeLabel).map(
                            (k) => ({
                                value: k,
                                name: ProductHistoricTypeLabel[k],
                            })
                        ),
                        selectedValues: productHistoricFilters.type
                            ? [
                                  {
                                      value: productHistoricFilters.type,
                                      name: ProductHistoricTypeLabel[
                                          productHistoricFilters.type
                                      ],
                                  },
                              ]
                            : [],
                    },
                    {
                        name: 'Usuário',
                        labelField: 'name',
                        valueField: 'value',
                        values: users.map((u) => ({
                            value: u.id,
                            name: u.name,
                        })),
                        selectedValues:
                            productHistoricFilters.users?.map((u) => ({
                                value: u.id,
                                name: u.name,
                            })) ?? [],
                        autoComplete: {
                            cb: (v) => {
                                clearTimeout(usersTimeout.current)
                                usersTimeout.current = setTimeout(() => {
                                    searchAccounts(v)
                                }, 500)
                            },
                        },
                    },
                    ...(canSeeAllBranches
                        ? [
                              {
                                  name: 'Filial',
                                  labelField: 'name',
                                  valueField: 'value',
                                  values: branches.map((b) => ({
                                      value: b.id,
                                      name: b.name,
                                  })),
                                  selectedValues:
                                      productHistoricFilters.branches?.map(
                                          (b) => ({
                                              value: b.id,
                                              name: b.name,
                                          })
                                      ) ?? [],
                              },
                          ]
                        : []),
                ]}
                search={''}
                withoutPaper
                hideSearch
                withoutSelection
                handleChangeSearch={(v) => {}}
                handleChangeSelectedFilter={(selectedFilter) => {
                    if (selectedFilter.name === 'Tipo') {
                        let newFilter: any = null
                        if (selectedFilter.action === 'add')
                            newFilter = selectedFilter.value

                        setProductHistoricFilters({
                            ...productHistoricFilters,
                            type: newFilter ? newFilter.value : undefined,
                        })
                        handleListProductHistoric({
                            ...productHistoricFilters,
                            type: newFilter ? newFilter.value : undefined,
                            page: 1,
                        })
                    }
                    if (selectedFilter.name === 'Filial') {
                        const current = productHistoricFilters.branches ?? []
                        let newBranches: BranchInfo[] = current
                        if (selectedFilter.action === 'remove') {
                            newBranches = current.filter(
                                (b) => b.id !== selectedFilter.value
                            )
                        } else {
                            const branch = branches.find(
                                (b) =>
                                    b.id ===
                                    (selectedFilter.value as any).value
                            )
                            if (
                                branch &&
                                !current.some((b) => b.id === branch.id)
                            )
                                newBranches = [...current, branch]
                        }
                        setProductHistoricFilters({
                            ...productHistoricFilters,
                            branches: newBranches,
                        })
                        handleListProductHistoric({
                            ...productHistoricFilters,
                            branches: newBranches,
                            page: 1,
                        })
                    }
                    if (selectedFilter.name === 'Usuário') {
                        const current = productHistoricFilters.users ?? []
                        let newUsers: AccountInfo[] = current
                        if (selectedFilter.action === 'remove') {
                            newUsers = current.filter(
                                (u) => u.id !== selectedFilter.value
                            )
                        } else {
                            const user = users.find(
                                (u) =>
                                    u.id ===
                                    (selectedFilter.value as any).value
                            )
                            if (
                                user &&
                                !current.some((u) => u.id === user.id)
                            )
                                newUsers = [...current, user]
                        }
                        setProductHistoricFilters({
                            ...productHistoricFilters,
                            users: newUsers,
                        })
                        handleListProductHistoric({
                            ...productHistoricFilters,
                            users: newUsers,
                            page: 1,
                        })
                    }
                }}
                handleChangeSelectedValues={() => {}}
                selectedValues={[]}
                identifier="id"
                values={productHistoricPaginated}
                sx={{ mt: '15px' }}
                pagination={{
                    page: productHistoricFilters.page,
                    rows: productHistoricFilters.rows,
                    rowsCount: productHistoricFilters.rowsCount,
                    handleChangePagination: (page, rows) => {
                        setProductHistoricFilters((filters) => ({
                            ...filters,
                            page,
                            rows,
                        }))
                    },
                }}
                columns={[
                    {
                        name: 'Tipo',
                        property: ['type'],
                        parser: (v) => ProductHistoricTypeLabel[v],
                        customRender: (
                            productHistoric: ProductHistoricInfo,
                            value
                        ) => {
                            const text = t(value)
                            const subtext = `${t('Número')}: ${
                                productHistoric.operationId
                            }`

                            return (
                                <Stack direction={'column'}>
                                    <Tooltip title={text}>
                                        <Typography fontSize={'0.875rem'}>
                                            {text}
                                        </Typography>
                                    </Tooltip>
                                    <Tooltip title={subtext}>
                                        <Typography
                                            fontSize={'0.75rem'}
                                            color={'#71797E'}
                                        >
                                            {subtext}
                                        </Typography>
                                    </Tooltip>
                                </Stack>
                            )
                        },
                    },
                    {
                        name: 'Fecha',
                        property: ['date'],
                        customRender: (
                            productHistoric: ProductHistoricInfo,
                            value
                        ) => {
                            const text = getDateString({
                                date: productHistoric.date ?? '',
                                hour: productHistoric.hour ?? '',
                                configs: configs,
                                isSummerTime:
                                    productHistoric.isSummerTime ?? false,
                            })
                            const timeagoText = `${t('Hace')} ${getTimeAgo({
                                date: `${productHistoric.date?.split('T')[0]} ${
                                    productHistoric.hour
                                }`,
                                configs: configs,
                                isSummerTime:
                                    productHistoric.isSummerTime ?? false,
                            })}`

                            return (
                                <Stack direction={'column'}>
                                    <Tooltip title={text}>
                                        <Typography fontSize={'0.875rem'}>
                                            {text}
                                        </Typography>
                                    </Tooltip>
                                    <Tooltip title={timeagoText}>
                                        <Typography
                                            fontSize={'0.75rem'}
                                            color={'#71797E'}
                                        >
                                            {timeagoText}
                                        </Typography>
                                    </Tooltip>
                                </Stack>
                            )
                        },
                    },
                    {
                        name: 'Valor',
                        property: ['value'],
                        customRender: (
                            productHistoric: ProductHistoricInfo,
                            value
                        ) => {
                            const text = productHistoric.coin
                                ? `${productHistoric.coin
                                      ?.symbol} ${maskCurrency(
                                      productHistoric.value,
                                      productHistoric.coin
                                  )}`
                                : '---'

                            return (
                                <Stack
                                    direction={'row'}
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                >
                                    <Tooltip title={text}>
                                        <Typography fontSize={'0.875rem'}>
                                            {text}
                                        </Typography>
                                    </Tooltip>

                                    {productHistoric.exchange && (
                                        <IconButton
                                            onClick={(e) => {
                                                setAnchor(e.currentTarget)
                                                selectedProductHistoric.current =
                                                    productHistoric
                                            }}
                                        >
                                            <CurrencyExchangeIcon />
                                        </IconButton>
                                    )}
                                    {productHistoric.type === 'entrada' && (
                                        <Tooltip
                                            title={
                                                <Stack>
                                                    <Typography>
                                                        Costo CIF:{' '}
                                                        {
                                                            productHistoric.coin
                                                                ?.symbol
                                                        }{' '}
                                                        {maskCurrency(
                                                            currency(
                                                                productHistoric.value
                                                            ).add(
                                                                productHistoric.cifPrice
                                                            ).value,
                                                            productHistoric.coin
                                                        )}
                                                    </Typography>
                                                    <Typography>
                                                        Costo médio:{' '}
                                                        {
                                                            productHistoric.coin
                                                                ?.symbol
                                                        }{' '}
                                                        {maskCurrency(
                                                            productHistoric.avgBuyPrice,
                                                            productHistoric.coin
                                                        )}
                                                    </Typography>
                                                    <Typography>
                                                        Costo CIF médio:{' '}
                                                        {
                                                            productHistoric.coin
                                                                ?.symbol
                                                        }{' '}
                                                        {maskCurrency(
                                                            currency(
                                                                productHistoric.avgBuyPrice
                                                            ).add(
                                                                productHistoric.avgCifPrice
                                                            ).value,
                                                            productHistoric.coin
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            }
                                        >
                                            <InfoRounded
                                                color="warning"
                                                sx={{
                                                    ml: '3px',
                                                    transform: 'scale(0.8)',
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Stack>
                            )
                        },
                    },
                    {
                        name: 'Cantidad',
                        property: ['qnt'],
                    },
                    {
                        name: 'Cliente/Proveedor',
                        property: ['agentName'],
                        parser: (v) => v ?? '---',
                    },
                    {
                        name: 'Vendedor',
                        property: ['employeeName'],
                        parser: (v) => v ?? '---',
                    },
                    ...productHistoricbranchColumn,
                ]}
            />
            <Stack>
                {productHistoricTotals.map((total) => (
                    <Stack direction={'row'} spacing={'10px'}>
                        {!!total.branch && (
                            <Typography fontWeight={'bold'}>
                                {total.branch?.name}:
                            </Typography>
                        )}
                        <Typography>
                            {t('Stock inicial')}:{' '}
                            <Box component={'span'} fontWeight={'bold'}>
                                {total.initialStock} {product.unitType}
                            </Box>
                        </Typography>
                        <Typography>
                            {t('Entrada total')}:{' '}
                            <Box component={'span'} fontWeight={'bold'}>
                                {total.totalIn} {product.unitType}
                            </Box>
                        </Typography>
                        <Typography>
                            {t('Salída total')}:{' '}
                            <Box component={'span'} fontWeight={'bold'}>
                                {total.totalOut} {product.unitType}
                            </Box>
                        </Typography>
                        <Typography>
                            {t('Stock atual')}:{' '}
                            <Box component={'span'} fontWeight={'bold'}>
                                {total.currentStock} {product.unitType}
                            </Box>
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </>
    )
}
